import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { isExistingAccountError, getAuthErrorTitle, getAuthErrorMessage } from '@/lib/utils/auth-error-handling';

interface UseGoogleOAuthOptions {
  redirectTo?: string;
  onSuccess?: () => void;
  onError?: (error: any) => void;
  onExistingAccount?: () => void; // New callback for existing account detection
  customToast?: {
    error: (message: string, options?: any) => void;
  };
}

export function useGoogleOAuth(options: UseGoogleOAuthOptions = {}) {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  
  // Use custom toast if provided, otherwise use the default toast
  const showToast = options.customToast || toast;

  const signInWithGoogle = async (isSignUp: boolean = false, customOptions?: Partial<UseGoogleOAuthOptions>) => {
    // Merge custom options with default options
    const finalOptions = { ...options, ...customOptions };
    
    try {
      setLoading(true);
      
      // Set OAuth flow flag for signup flows
      if (isSignUp) {
        sessionStorage.setItem('oauth_flow', 'true');
      } else {
        // Clear any existing OAuth flow flag for login flows
        sessionStorage.removeItem('oauth_flow');
      }
      
      // Determine the correct redirect URL
      let redirectUrl = finalOptions.redirectTo;
      if (!redirectUrl) {
        if (isSignUp) {
          redirectUrl = `${window.location.origin}/onboarding`;
        } else {
          // For login flows, redirect to the intended destination or default to /meeting
          redirectUrl = `${window.location.origin}/meeting`;
        }
      }
      
      console.log(`[useGoogleOAuth] Initiating ${isSignUp ? 'signup' : 'login'} flow with redirect to:`, redirectUrl);
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          queryParams: {
            prompt: 'select_account', // Force account selection
          }
        }
      });

      if (error) {
        console.error('Google OAuth error:', error);
        
        // Check if this is an existing account error
        if (isExistingAccountError(error) && isSignUp) {
          // Call the existing account callback if provided
          if (finalOptions.onExistingAccount) {
            finalOptions.onExistingAccount();
            return;
          }
          
          // Fallback to showing toast and redirecting
          if (finalOptions.customToast && typeof showToast === 'object' && 'error' in showToast) {
            showToast.error(getAuthErrorTitle(error), {
              description: getAuthErrorMessage(error),
            });
          } else if (typeof showToast === 'function') {
            showToast({
              title: getAuthErrorTitle(error),
              description: getAuthErrorMessage(error),
            });
          }
          
          // Redirect to login page
          navigate('/login?message=Please sign in with your existing account');
        } else {
          if (finalOptions.customToast && typeof showToast === 'object' && 'error' in showToast) {
            showToast.error(getAuthErrorTitle(error), {
              description: getAuthErrorMessage(error),
            });
          } else if (typeof showToast === 'function') {
            showToast({
              title: getAuthErrorTitle(error),
              description: getAuthErrorMessage(error),
              variant: "destructive",
            });
          }
        }
        
        finalOptions.onError?.(error);
      } else {
        finalOptions.onSuccess?.();
      }
    } catch (error: any) {
      console.error('Google OAuth error:', error);
      
      // Handle any other errors that might occur
      if (finalOptions.customToast && typeof showToast === 'object' && 'error' in showToast) {
        showToast.error("Authentication Failed", {
          description: error.message || "Failed to authenticate with Google. Please try again.",
        });
      } else if (typeof showToast === 'function') {
        showToast({
          title: "Authentication Failed",
          description: error.message || "Failed to authenticate with Google. Please try again.",
          variant: "destructive",
        });
      }
      
      finalOptions.onError?.(error);
    } finally {
      setLoading(false);
    }
  };

  return {
    signInWithGoogle,
    loading
  };
} 